import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { X, Check, Loader2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { trackCropperEvent } from "@/lib/analytics";

/**
 * CoverPhotoCropper — 16:9 crop + rotate modal for business cover photos.
 *
 * Why 16:9: the mobile app's business-detail hero is a wide landscape band.
 * Portrait or square uploads get letterboxed with ugly background bars, so
 * we lock the crop frame at 16:9 and let the user pan/zoom the source
 * photo until it fills the frame.
 *
 * Flow:
 *   1. User picks a file → parent opens this modal with `imageSrc` set.
 *   2. User pans / zooms / rotates until happy with the 16:9 frame.
 *   3. User clicks "Use this crop" → we rasterise the visible frame on an
 *      offscreen canvas and hand the resulting Blob to `onCropComplete`.
 *   4. Parent uploads the Blob via /api/uploads (unchanged backend).
 *
 * `onCropComplete(blob: Blob)` — the cropped JPEG, ~1600x900 (always 16:9).
 * `onCancel()` — close the modal, discard the source photo.
 */
export default function CoverPhotoCropper({ imageSrc, onCropComplete, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [busy, setBusy] = useState(false);
  const croppedAreaPixelsRef = useRef(null);

  // Wrap the parent's onCancel so every dismissal path (X, Cancel button,
  // backdrop click) fires one — and only one — "cancelled" funnel event.
  // Flag prevents double-firing if the user mashes the X twice.
  const cancelFiredRef = useRef(false);
  const handleCancel = useCallback(() => {
    if (!cancelFiredRef.current) {
      cancelFiredRef.current = true;
      trackCropperEvent("cancelled");
    }
    onCancel();
  }, [onCancel]);

  const onCropComplete_ = useCallback((_, croppedAreaPixels) => {
    // Stashed in a ref (not state) so the "Use this crop" button handler
    // reads the latest value without re-rendering the cropper on every
    // pointermove tick — otherwise the drag lags.
    croppedAreaPixelsRef.current = croppedAreaPixels;
  }, []);

  const confirm = async () => {
    if (!croppedAreaPixelsRef.current) return;
    // Fire funnel event BEFORE the heavy rasterise + upload. "confirmed"
    // measures intent to use the crop; "upload_succeeded" / "_failed" is
    // the next step, fired by the parent on upload resolution.
    trackCropperEvent("confirmed");
    setBusy(true);
    try {
      const blob = await rasterise(imageSrc, croppedAreaPixelsRef.current, rotation);
      onCropComplete(blob);
    } catch (e) {
      // Non-fatal — leave the modal open so the user can retry.
      // eslint-disable-next-line no-console
      console.error("[cover-crop] rasterise failed", e);
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="cover-photo-cropper-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) handleCancel(); }}
    >
      <div className="relative w-full max-w-3xl bg-background border border-border">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 md:px-6">
          <div>
            <h3 className="text-base font-semibold">Crop your cover photo</h3>
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
              16 : 9 · the mobile app hero shape
            </p>
          </div>
          <button
            type="button"
            data-testid="cover-cropper-close"
            onClick={handleCancel}
            disabled={busy}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* The cropper itself needs a parent with explicit height for its
            internal absolute-positioned canvas. 60vh works on both desktop
            and mobile portrait. */}
        <div className="relative h-[60vh] bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete_}
            showGrid={true}
            // Dark overlay so the user can clearly see the 16:9 safe area.
            style={{ containerStyle: { background: "#000" } }}
          />
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-3 border-t border-border p-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex items-center gap-3">
            <ZoomOut size={14} className="text-muted-foreground" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="cover-cropper-zoom"
              aria-label="Zoom"
              className="w-40 accent-[#C9A85F]"
            />
            <ZoomIn size={14} className="text-muted-foreground" />
            <button
              type="button"
              data-testid="cover-cropper-rotate"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="mono inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
            >
              <RotateCcw size={11} /> Rotate 90°
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="cover-cropper-cancel"
              onClick={handleCancel}
              disabled={busy}
              className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground px-3 py-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="cover-cropper-confirm"
              onClick={confirm}
              disabled={busy}
              className="inline-flex items-center gap-2 gold-metallic-bg px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-black transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {busy ? "Rasterising…" : "Use this crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Rasterise the visible crop frame onto an offscreen canvas and return a
 * JPEG Blob. Handles rotation by drawing to an intermediate canvas sized
 * to the rotated bounding box, then slicing the crop rect out of that.
 *
 * Output: fixed-aspect 16:9 JPEG. We clamp the long edge to 1600px so the
 * uploaded file stays well under the 5MB upload limit for most photos.
 */
async function rasterise(imageSrc, pixelCrop, rotation = 0) {
  const image = await loadImage(imageSrc);
  const radians = (rotation * Math.PI) / 180;

  // Rotated canvas large enough to hold the full rotated source so the
  // crop rect is addressable in rotated-image space.
  const safeArea = Math.max(image.width, image.height) * 2;
  const canvas = document.createElement("canvas");
  canvas.width = safeArea;
  canvas.height = safeArea;
  const ctx = canvas.getContext("2d");
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(radians);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(
    image,
    safeArea / 2 - image.width / 2,
    safeArea / 2 - image.height / 2,
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // Final canvas, exactly the crop rect size.
  const out = document.createElement("canvas");
  out.width = pixelCrop.width;
  out.height = pixelCrop.height;
  const outCtx = out.getContext("2d");
  outCtx.putImageData(
    data,
    -safeArea / 2 + image.width / 2 - pixelCrop.x,
    -safeArea / 2 + image.height / 2 - pixelCrop.y,
  );

  // Downscale to 1600px wide (keeping 16:9) so even a 4K crop stays
  // under the 5MB upload cap in JPEG. Skip if already smaller.
  const MAX_W = 1600;
  let finalCanvas = out;
  if (out.width > MAX_W) {
    const resized = document.createElement("canvas");
    resized.width = MAX_W;
    resized.height = Math.round(MAX_W * (out.height / out.width));
    const rctx = resized.getContext("2d");
    rctx.imageSmoothingQuality = "high";
    rctx.drawImage(out, 0, 0, resized.width, resized.height);
    finalCanvas = resized;
  }

  return new Promise((resolve, reject) => {
    finalCanvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Blob encode failed"))),
      "image/jpeg",
      0.92,
    );
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = src;
  });
}
